/**
 * LightingUniforms.ts — shared uniform buffer with sun, ambient, rim, fog.
 *
 * Read by every render pass that shades geometry. Preset: WARM AFTERNOON.
 * Sun azimuth 45° (from south-east), elevation 50°.
 *
 * WGSL layout (std140-ish, 16-byte aligned):
 *   sunDir         : vec3<f32>  (world-space, points TOWARD the sun)
 *   _pad0          : f32
 *   sunColor       : vec3<f32>  (linear)
 *   sunIntensity   : f32
 *   ambientColor   : vec3<f32>
 *   _pad1          : f32
 *   rimColor       : vec3<f32>
 *   rimStrength    : f32
 *   fogColor       : vec3<f32>  (typically palette.skyHorizon)
 *   _pad2          : f32
 *   fogStart       : f32
 *   fogEnd         : f32
 *   fogStrength    : f32
 *   _pad3          : f32
 *
 * Total: 96 bytes. Aligned size: 96.
 */

import { createUniformBuffer } from "../gpu/Buffers";

const STRUCT_SIZE = 96;

export interface LightingState {
  /** Sun azimuth in radians. Meteorological convention: 0 = north, π/2 = east. */
  sunAzimuth: number;
  /** Sun elevation in radians above horizon. */
  sunElevation: number;
  sunColor: [number, number, number];
  sunIntensity: number;
  ambientColor: [number, number, number];
  rimColor: [number, number, number];
  rimStrength: number;
  fogColor: [number, number, number];
  fogStart: number;
  fogEnd: number;
  fogStrength: number;
  /** 1/m density for low height fog. 0 disables. */
  heightFogDensity: number;
  /** Maximum height-fog tint contribution (0..1). */
  heightFogStrength: number;
}

/**
 * Warm afternoon preset (~15:00–16:00). Stage-1 default.
 * Colors are linear (rgba16float HDR target → tonemap in BlitPass).
 * Source values are sRGB hex from the brief, converted to linear.
 */
export function defaultLightingState(): LightingState {
  return {
    sunAzimuth: deg2rad(45),
    sunElevation: deg2rad(50),
    sunColor: srgbHexToLinear("#FFE5B4"),
    sunIntensity: 1.0,
    ambientColor: srgbHexToLinear("#4A5568"),
    rimColor: srgbHexToLinear("#FFC78A"),
    rimStrength: 0.6,
    fogColor: srgbHexToLinear("#A7B4AE"),
    fogStart: 220.0,
    fogEnd: 1350.0,
    fogStrength: 0.82,
    heightFogDensity: 0.035,
    heightFogStrength: 0.42,
  };
}

export class LightingUniforms {
  readonly buffer: GPUBuffer;
  readonly state: LightingState;
  private cpu = new ArrayBuffer(STRUCT_SIZE);
  private f32 = new Float32Array(this.cpu);

  constructor(
    private device: GPUDevice,
    state: LightingState = defaultLightingState(),
  ) {
    this.buffer = createUniformBuffer(device, STRUCT_SIZE, "LightingUniforms");
    this.state = state;
    this.upload();
  }

  /** Recompute sun direction from azimuth/elevation, write the whole struct. */
  upload(): void {
    const s = this.state;
    const sunDir = sunDirFromAzEl(s.sunAzimuth, s.sunElevation);
    let i = 0;
    // sunDir + pad
    this.f32[i++] = sunDir[0];
    this.f32[i++] = sunDir[1];
    this.f32[i++] = sunDir[2];
    this.f32[i++] = 0;
    // sunColor + intensity
    this.f32[i++] = s.sunColor[0];
    this.f32[i++] = s.sunColor[1];
    this.f32[i++] = s.sunColor[2];
    this.f32[i++] = s.sunIntensity;
    // ambient + pad
    this.f32[i++] = s.ambientColor[0];
    this.f32[i++] = s.ambientColor[1];
    this.f32[i++] = s.ambientColor[2];
    this.f32[i++] = 0;
    // rim + strength
    this.f32[i++] = s.rimColor[0];
    this.f32[i++] = s.rimColor[1];
    this.f32[i++] = s.rimColor[2];
    this.f32[i++] = s.rimStrength;
    // fog color + heightFogDensity
    this.f32[i++] = s.fogColor[0];
    this.f32[i++] = s.fogColor[1];
    this.f32[i++] = s.fogColor[2];
    this.f32[i++] = s.heightFogDensity;
    // fog params + heightFogStrength
    this.f32[i++] = s.fogStart;
    this.f32[i++] = s.fogEnd;
    this.f32[i++] = s.fogStrength;
    this.f32[i++] = s.heightFogStrength;
    this.device.queue.writeBuffer(this.buffer, 0, this.cpu);
  }

  destroy(): void {
    this.buffer.destroy();
  }
}

/**
 * Sun direction (pointing TOWARD the sun, world space).
 * Azimuth meteo: 0 = north (+Z if north is +Z), 90° = east.
 * We define: north = -Z, east = +X (right-handed, Y-up).
 *   sunDir.xz at horizon = (sin(az), -cos(az))
 * elevation rotates up around horizontal direction.
 */
export function sunDirFromAzEl(
  az: number,
  el: number,
): [number, number, number] {
  const ce = Math.cos(el);
  const se = Math.sin(el);
  const x = ce * Math.sin(az);
  const z = ce * -Math.cos(az);
  const y = se;
  return [x, y, z];
}

export function deg2rad(d: number): number {
  return (d * Math.PI) / 180;
}

export function srgbHexToLinear(hex: string): [number, number, number] {
  const m = hex.replace("#", "");
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  return [srgbToLinear(r), srgbToLinear(g), srgbToLinear(b)];
}

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
